import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import {
  type VideoCreditTrialActions,
  VideoCreditTrialGuidance,
} from '@/components/shared/video-credit-trial-guidance';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getPricingFaqs } from '@/config/pricing-config';
import { getAllCreditPackages } from '@/credits/packages';
import { getRecommendedCreditPackage } from '@/lib/credit-top-up';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';
import { OneTimePricingCards } from './one-time-pricing-cards';

/** Insufficient-credit dialog recommends among these packs. */
const CREDITS_DIALOG_PAYG_IDS = [
  'starter-drop',
  'creator-stack',
  'studio-vault',
];

/** Recommend the smallest pack that makes this one run affordable. */
const CREDITS_DIALOG_INTENDED_RUNS = 1;

export function PublicCreditPackages({
  checkoutMetadata,
  compact = false,
  onBeforeCheckout,
  returnTo,
  returnUrl,
}: {
  checkoutMetadata?: Record<string, string>;
  compact?: boolean;
  onBeforeCheckout?: () => void | Promise<void>;
  returnTo?: string;
  returnUrl?: string;
}) {
  return (
    <div className={cn('space-y-8', compact && 'space-y-6')}>
      <OneTimePricingCards
        checkoutMetadata={checkoutMetadata}
        compact={compact}
        onBeforeCheckout={onBeforeCheckout}
        returnTo={returnTo}
        returnUrl={returnUrl}
        source={compact ? 'credits_dialog' : 'pricing_page'}
      />
      <p className="mx-auto max-w-3xl text-center text-white/45 text-xs leading-6">
        {m.pricing_public_usage_note()}
      </p>
    </div>
  );
}

export function PricingFaqSection() {
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const faqs = getPricingFaqs().map((faq, index) => ({
    id: String(index + 1),
    ...faq,
  }));

  return (
    <section className="px-0 py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <h2 className="mb-8 text-center font-semibold text-white text-xl md:mb-10 md:text-2xl lg:text-3xl">
          {m.pricing_public_faq_heading()}
        </h2>

        <div>
          {faqs.map((faq) => {
            const isOpen = openFaqId === faq.id;
            const triggerId = `pricing-faq-trigger-${faq.id}`;
            const panelId = `pricing-faq-panel-${faq.id}`;

            return (
              <div key={faq.id} className="border-white/10 border-b">
                <button
                  id={triggerId}
                  type="button"
                  aria-controls={panelId}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center justify-between py-4 text-left font-medium text-sm text-white transition hover:text-[#8ef25a]"
                  onClick={() =>
                    setOpenFaqId((current) =>
                      current === faq.id ? null : faq.id
                    )
                  }
                >
                  {faq.question}
                  <IconChevronDown
                    className={cn(
                      'size-4 text-white/40 transition',
                      isOpen && 'rotate-180'
                    )}
                  />
                </button>
                <section
                  id={panelId}
                  aria-hidden={!isOpen}
                  aria-labelledby={triggerId}
                  className={cn(
                    'grid text-sm text-white/60 leading-7 opacity-0 transition-[grid-template-rows,opacity] duration-200',
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="pb-4">{faq.answer}</p>
                  </div>
                </section>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PayAsYouGoPackages({
  checkoutMetadata,
  onBeforeCheckout,
  recommendedId,
  returnTo,
}: {
  checkoutMetadata: Record<string, string>;
  onBeforeCheckout?: () => void | Promise<void>;
  recommendedId?: string;
  returnTo?: string;
}) {
  return (
    <OneTimePricingCards
      checkoutMetadata={checkoutMetadata}
      compact
      onBeforeCheckout={onBeforeCheckout}
      recommendedId={recommendedId}
      returnTo={returnTo}
      source="credits_required_dialog"
    />
  );
}

export function CreditsRequiredDialog({
  balance,
  cost,
  modelLabel,
  onAdjustSettings,
  onOpenChange,
  onViewCredits,
  open,
  toolLabel,
  returnTo,
  onBeforeCheckout,
  videoTrial,
}: {
  balance?: number;
  cost: number;
  modelLabel?: string;
  onAdjustSettings?: () => void;
  onOpenChange: (open: boolean) => void;
  onViewCredits: () => void;
  open: boolean;
  toolLabel?: string;
  returnTo?: string;
  onBeforeCheckout?: () => void | Promise<void>;
  videoTrial?: VideoCreditTrialActions;
}) {
  const balanceLabel =
    typeof balance === 'number' ? balance.toLocaleString() : '--';
  const checkoutMetadata: Record<string, string> = {
    source: 'credits_required_dialog',
    requiredCredits: String(cost),
    availableCredits: String(balance ?? 0),
  };
  const recommendedPackage = getRecommendedCreditPackage(
    getAllCreditPackages().filter((pkg) =>
      CREDITS_DIALOG_PAYG_IDS.includes(pkg.id)
    ),
    {
      actionCost: cost,
      balance: balance ?? 0,
      intendedRuns: CREDITS_DIALOG_INTENDED_RUNS,
    }
  );
  if (recommendedPackage) {
    checkoutMetadata.recommendedPackageId = recommendedPackage.id;
  }
  if (toolLabel) checkoutMetadata.tool = toolLabel;
  if (modelLabel) checkoutMetadata.model = modelLabel;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0f0f0f] pt-8 text-white sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-sans text-xl">
            {m.pricing_public_credits_required()}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {m.pricing_public_generation_needs({
              balance: balanceLabel,
              cost: cost.toLocaleString(),
            })}
          </DialogDescription>
        </DialogHeader>
        {videoTrial ? (
          <VideoCreditTrialGuidance
            {...videoTrial}
            cost={cost}
            presentation="pricing"
          />
        ) : null}
        <PayAsYouGoPackages
          checkoutMetadata={checkoutMetadata}
          recommendedId={recommendedPackage?.id}
          returnTo={returnTo}
          onBeforeCheckout={onBeforeCheckout}
        />
        <p className="text-center text-white/45 text-xs">
          {m.pricing_trust_one_time()} · {m.pricing_trust_never_expire()} ·{' '}
          {m.pricing_trust_refunds()}
        </p>
        <DialogFooter className="border-white/10 bg-white/[0.03]">
          {onAdjustSettings ? (
            <Button
              type="button"
              variant="ghost"
              className="text-white/70 hover:bg-white/10 hover:text-white"
              onClick={onAdjustSettings}
            >
              {m.pricing_public_adjust_settings()}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="text-white/70 hover:bg-white/10 hover:text-white"
            onClick={onViewCredits}
          >
            {m.pricing_public_view_credits()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreditPackagesDialog({
  checkoutMetadata,
  description,
  onBeforeCheckout,
  onOpenChange,
  open,
  returnTo,
  title,
}: {
  checkoutMetadata?: Record<string, string>;
  description?: string;
  onBeforeCheckout?: () => void | Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  returnTo?: string;
  title?: string;
}) {
  const dialogTitle = title ?? m.pricing_public_upgrade_credits();
  const dialogDescription =
    description ?? m.pricing_public_upgrade_description();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0f0f0f] pt-8 text-white sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-sans text-xl">{dialogTitle}</DialogTitle>
          <DialogDescription className="text-white/60">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>
        <PublicCreditPackages
          checkoutMetadata={checkoutMetadata}
          compact
          onBeforeCheckout={onBeforeCheckout}
          returnTo={returnTo}
        />
      </DialogContent>
    </Dialog>
  );
}
